import { useEffect, useRef, useState, type FormEvent } from 'react';
import { useSession } from '../../state/useSession';
import { Button, TextField } from '../../ui';
import { PASSWORD_RULE } from '../auth/passwordRule';

/**
 * Account → Change password, ported from src/components/account/ChangePasswordModal.tsx.
 *
 * The same two steps and the same words: a code emailed to the signed-in address, then the code
 * and a new password. The phone does it this way because the pool's reset flow is the one path
 * that needs no current password, and the same is true here. Without it the web's only way to
 * change a password was to sign out and use "Forgot password" on the sign-in page.
 *
 * A <dialog>, like Delete account's: a native modal traps focus and Escape closes it. Escape
 * closes it without asking React, so the 'close' event is what tells the screen it is shut —
 * otherwise the next press of the row would set an `open` that was already true, and nothing
 * would open.
 */
export default function ChangePassword({
  open,
  email,
  onClose,
}: {
  open: boolean;
  email: string;
  onClose: () => void;
}) {
  const dialog = useRef<HTMLDialogElement>(null);
  const { forgotPassword, confirmForgotPassword } = useSession();
  const [stage, setStage] = useState<'request' | 'confirm' | 'done'>('request');
  const [code, setCode] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function close(): void {
    setStage('request');
    setCode('');
    setPassword('');
    setError(null);
    onClose();
  }

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    if (open && !d.open) d.showModal();
    if (!open && d.open) d.close();
  }, [open]);

  useEffect(() => {
    const d = dialog.current;
    if (!d) return;
    const shut = () => close();
    d.addEventListener('close', shut);
    return () => d.removeEventListener('close', shut);
  });

  async function request(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!email || busy) return;
    setError(null);
    setBusy(true);
    try {
      await forgotPassword(email);
      setStage('confirm');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not send a code.');
    } finally {
      setBusy(false);
    }
  }

  async function confirm(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!code.trim() || password.length < 8 || busy) return;
    setError(null);
    setBusy(true);
    try {
      await confirmForgotPassword(email, code.trim(), password);
      setStage('done');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not change your password.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <dialog ref={dialog} aria-label="Change password" className="confirm-dialog">
      {open && (
        <>
          <h2 className="type-title">Change password</h2>
          {error && <p role="alert" className="type-caption field-error-text">{error}</p>}
          {stage === 'done' ? (
            <>
              <p role="status" className="type-body">Your password has been changed.</p>
              <Button title="Done" onPress={close} />
            </>
          ) : stage === 'request' ? (
            <form onSubmit={(e) => void request(e)} noValidate>
              <p className="type-body">We'll email a verification code to {email}.</p>
              <div className="build-actions">
                <Button title="Send code" type="submit" loading={busy} />
                <Button title="Cancel" variant="ghost" onPress={close} />
              </div>
            </form>
          ) : (
            <form onSubmit={(e) => void confirm(e)} noValidate>
              <TextField label="Verification code" value={code} onChangeText={setCode} placeholder="6-digit code" />
              <TextField
                label="New password"
                type="password"
                value={password}
                onChangeText={setPassword}
                hint={PASSWORD_RULE}
              />
              <div className="build-actions">
                <Button
                  title="Change password"
                  type="submit"
                  loading={busy}
                  disabled={!code.trim() || password.length < 8}
                />
                <Button title="Cancel" variant="ghost" onPress={close} />
              </div>
            </form>
          )}
        </>
      )}
    </dialog>
  );
}
